import type { ToolDef } from "./catalog";

export interface ToolRunContext {
  hasCase: boolean;
  hasInput: boolean;
  hasReference: boolean;
  busy: boolean;
  jobRunning: boolean;
  engineReady: boolean;
  pipelineKind?: string;
}

/** Shared by the button and execution path, including command-palette runs. */
export function toolRunIssue(tool: ToolDef | undefined, ctx: ToolRunContext): string | null {
  if (!tool) return "Choose an imaging tool.";
  if (ctx.busy) return "An image operation is in progress. Wait for it to finish.";
  if (tool.status !== "ready") return tool.comingSoon ?? "This tool is not available yet.";
  if (!ctx.hasCase) return "Open a study first.";
  if (!tool.stage && (tool.op || tool.inspect === "header") && !ctx.hasInput) return "Select an image, mask or atlas in the Study Explorer.";
  if (tool.needsReference && !ctx.hasReference) return "Select a reference image from this study.";
  if (tool.stage?.modality === "PWI" && ctx.pipelineKind === "dwi") return "This stage requires a PWI or combined study.";
  if (tool.stage?.modality === "DWI" && ctx.pipelineKind === "pwi") return "This stage requires a DWI or combined study.";
  if (tool.op || tool.stage) {
    if (ctx.jobRunning) return "Wait for the active pipeline job to finish.";
    if (!ctx.engineReady)
      return "No processing engine is ready. Open Developer → Engine to set one up.";
  }
  return null;
}

export function parameterIssue(tool: ToolDef, values: Record<string, string>): string | null {
  for (const field of tool.fields ?? []) {
    const raw = values[`${tool.id}.${field.id}`] ?? field.value;
    if (field.kind === "number") {
      const parts = raw.split(",");
      if (parts.some((part) => part.trim() === "" || !Number.isFinite(Number(part)))) return `${field.label}: enter finite numeric values.`;
    } else if (field.options && !field.options.some((option) => option.value === raw)) {
      return `${field.label}: choose an available option.`;
    }
  }
  return null;
}
